import { useCallback, useSyncExternalStore } from 'react';
import { useAppSession } from './useAppSession';

const CHANGE_EVENT = 'overlayqa:install-reminder-dismissed';
const sessionDismissals = new Set<string>();

function subscribe(onChange: () => void) {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Dashboard and global reminders share one account-specific preference.
 * The pending onboarding handoff does not use it: that prompt completes an
 * explicit request to open a site (DashboardPage's hasPendingOpen branch).
 */
export function useInstallReminderDismissal() {
  const { data: session } = useAppSession();
  const key = session?.user.id ? `oqa:install-reminder-dismissed:${session.user.id}` : null;
  const getSnapshot = useCallback(() => {
    if (!key) return false;
    if (sessionDismissals.has(key)) return true;
    try { return localStorage.getItem(key) === '1'; } catch { return false; }
  }, [key]);
  const dismissed = useSyncExternalStore(subscribe, getSnapshot, () => false);
  const dismiss = useCallback(() => {
    if (!key) return;
    try { localStorage.setItem(key, '1'); } catch {
      // Keep dismissal for this app session even when browser storage refuses.
      sessionDismissals.add(key);
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, [key]);
  return { dismissed, dismiss };
}
